/* app/discovery-strip.jsx - the strip that tells a screen what it is not showing.

   Reads GET /api/inventory?site_id&module and renders one card per signal. The
   signals are deterministic findings computed from the database (see
   backend/lib/inventory.js), so nothing here is a guess and nothing here costs
   tokens.

   Three rules this component exists to keep:

   - A failed read is never silence. If the strip cannot load, it says so and
     offers a retry, because an empty strip on a broken request looks exactly
     like a clean bill of health.
   - A button only appears when the host screen actually handles that action.
     The strip does not know how to classify or plan anything; it renders the
     button only if the host passed a handler for that action kind, so there is
     no control here that does nothing when pressed.
   - The action receives the signal's own ids, so what a button acts on is
     exactly what its headline counted. When the id list is capped, the card
     says how many are covered rather than implying it handles all of them.
   - A signal that BLOCKS something says so. A count on its own reads as a
     chore, and some of these findings are the reason a later screen cannot
     work at all, so a card can carry a consequence line beside its detail. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  const TONE_ICON = { problem: "alert-triangle", opportunity: "trending-up", todo: "circle" };

  // What each action kind is called on the button. A signal whose action kind is
  // not in here gets no button even if the host passes a handler, because an
  // unlabelled control is worse than none.
  const ACTION_LABEL = {
    classify: "Classify these",
    plan    : "Plan these",
    filter  : "Show them",
    open    : "Review them",
    generate: "Generate drafts",
  };

  // What a signal BLOCKS, keyed by signal id.
  //
  // Separate from `detail` on purpose: detail says what the finding is, this
  // says what stops working while it stands. Most signals block nothing and get
  // no line. The one that does is unclassified keywords: facet assembly matches
  // material into a piece by category or cluster, and a raw keyword carries
  // neither, so it can never be pulled in and the piece it should have supplied
  // stays unable to generate a brief.
  //
  // It lives here rather than in a host screen so that every screen rendering a
  // signal gives the same reason, and so no screen can quietly give a different
  // one. A signal may also carry its own `consequence` from the server, which
  // wins: the finding and the reason it matters are then written in one place.
  const CONSEQUENCE = {
    "keywords-unclassified":
      "Facet assembly pulls keywords into a piece by matching category or cluster, and an unclassified keyword has neither, so nothing can pull these in. Until they are classified, a piece promoted from a topic keeps reporting that it has no keyword yet and cannot generate a brief.",
  };

  // A handler is looked up by SIGNAL ID first, then by action kind. Kind alone
  // was not enough: "Plan these" is right for unplanned topics and open gaps
  // and wrong for approved FAQs (a page per question is not how FAQs are used),
  // yet all three carry action kind `plan`. Keyed only by kind, a host had to
  // choose between one button that is wrong on one card and no button on any of
  // them. Per-signal keys let it wire what is right and leave the rest with no
  // button, which is the correct outcome rather than a compromise.
  /* ONE LINE PER SIGNAL, and the rest in a drawer.
   *
   * These cards carried a detail paragraph, a "what this blocks" paragraph and
   * a chip sample each, on three screens, above the work. Five findings filled
   * the screen before the table the user came for, so the strip that exists to
   * say what is unattended became the thing in the way. The finding is the
   * TITLE (it already carries the count); everything that explains it is one
   * click away and nothing was shortened to get there. The action button stays
   * on the line: it is the point of the signal, and burying it would cost a
   * click on the thing people came to press. */
  function SignalLine({ sig, actions, busy, onRun }) {
    const [open, setOpen] = useState(false);
    const handler = sig.action && (actions[sig.id] || actions[sig.action.kind]);
    const label = sig.action && ACTION_LABEL[sig.action.kind];
    const running = busy === sig.id;
    const blocks = sig.consequence || CONSEQUENCE[sig.id] || null;
    const DS = window.HuddleDesignSystem_775113;
    const Drawer = DS && DS.Drawer;
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [open]);
    const body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
        <p style={{ margin: 0 }}>{sig.detail}</p>
        {blocks && <p style={{ margin: 0 }}><b>What this blocks: </b>{blocks}</p>}
        {sig.sample && sig.sample.length > 0 && (
          <div className="ds-card__sample">
            {sig.sample.map((s, i) => <span className="ds-chip" key={s.id != null ? s.id : i} title={s.label}>{s.label}</span>)}
            {sig.count > sig.sample.length && <span className="ds-chip ds-chip--more">+{sig.count - sig.sample.length}</span>}
          </div>
        )}
        {handler && (
          <div className="ds-card__foot">
            <button className="bi-btn bi-btn--sm" disabled={running} onClick={() => { onRun(sig, handler); setOpen(false); }}>
              {running ? "Working..." : label}
            </button>
            {sig.capped && <span className="ds-card__note">acts on the first {sig.ids.length} of {sig.count}</span>}
          </div>
        )}
      </div>
    );
    return (
      <>
        <div className={`ds-line ds-line--${sig.tone}`} role="button" tabIndex={0}
             title="Open the details"
             onClick={() => setOpen(true)}
             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}>
          <span className="ds-card__icon"><I name={TONE_ICON[sig.tone] || "circle"} size={15} /></span>
          <span className="ds-line__title">{sig.title}</span>
          {handler && (
            <button className="bi-btn bi-btn--sm" disabled={running} onClick={(e) => { e.stopPropagation(); onRun(sig, handler); }}>
              {running ? "Working..." : label}
            </button>
          )}
          <I name="chevron-right" size={15} />
        </div>
        {open && (Drawer
          ? <Drawer title={sig.title} subtitle="What this is, and what it blocks" onClose={() => setOpen(false)}>{body}</Drawer>
          : <div className="ds-card" style={{ position: "fixed", right: 16, top: 72, width: 420, zIndex: 50 }}>
              <div className="ds-card__head"><span className="ds-card__title">{sig.title}</span><button className="bi-btn bi-btn--sm" onClick={() => setOpen(false)}><I name="x" size={14} /></button></div>
              {body}
            </div>)}
      </>
    );
  }

  /**
   * @param {number|null} siteId
   * @param {string} module   keywords | topics | planner
   * @param {object} actions  { classify?, plan?, filter?, open?, generate? }
   *                          each (signal) => Promise|void. Only the kinds
   *                          present here get a button.
   * @param {number} reloadKey  bump it after a write to recompute the signals
   */
  function DiscoveryStrip({ siteId, module, actions = {}, reloadKey = 0 }) {
    const [state, setState] = useState({ loading: true, error: null, signals: [] });
    const [open, setOpen] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
      if (siteId == null) { setState({ loading: false, error: null, signals: [] }); return; }
      setState(s => ({ ...s, loading: true, error: null }));
      try {
        const inv = await api(`/inventory?site_id=${siteId}&module=${module}`);
        setState({ loading: false, error: null, signals: Array.isArray(inv && inv.signals) ? inv.signals : [] });
      } catch (e) {
        setState({ loading: false, error: e.message || "Could not read the signals.", signals: [] });
      }
    }, [siteId, module]);
    useEffect(() => { load(); }, [load, reloadKey]);

    // The host does the work and reports back; the strip only recomputes after,
    // so a card cannot keep offering something that has just been done.
    const run = async (sig, handler) => {
      setBusy(sig.id);
      try { await handler(sig); } finally { setBusy(null); await load(); }
    };

    if (siteId == null) return null;
    if (state.loading && !state.signals.length) {
      return <div className="ds-strip ds-strip--quiet"><I name="loader" size={14} />Looking at what is unattended...</div>;
    }
    if (state.error) {
      return (
        <div className="ds-strip ds-strip--error">
          <I name="alert-triangle" size={14} />
          <span>Could not read the signals: {state.error}</span>
          <button className="bi-btn bi-btn--sm" onClick={load}>Try again</button>
        </div>
      );
    }
    // Said plainly rather than rendered as nothing: on a screen that sometimes
    // shows cards, an empty space is ambiguous.
    if (!state.signals.length) {
      return <div className="ds-strip ds-strip--quiet"><I name="check" size={14} />Nothing here is sitting unattended.</div>;
    }

    return (
      <div className="ds-strip">
        <button className="ds-strip__head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          <I name={open ? "chevron-down" : "chevron-right"} size={15} />
          <span className="ds-strip__title">{state.signals.length === 1 ? "1 thing to look at" : `${state.signals.length} things to look at`}</span>
          <span className="ds-strip__sub">from what is already in your banks and plans</span>
        </button>
        {open && (
          <div className="ds-lines">
            {state.signals.map(sig => (
              <SignalLine key={sig.id} sig={sig} actions={actions} busy={busy} onRun={run} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Where each module's signals live, so a nudge can offer to take you there.
  const MODULE_SCREEN = {
    keywords: ["keywords-planner", "Keywords Planner"],
    topics  : ["topics-bank", "Topics Bank"],
    planner : ["content-planner", "Content Planner"],
  };

  /**
   * One line, on the screen where the user just did something, pointing at what
   * it made possible.
   *
   * Event-driven on purpose. `trigger` is a number the host bumps AFTER a write
   * it caused, and the nudge reads only then. It never polls, nothing is stored
   * server side, and no AI runs, which is what makes it free enough to sit on a
   * screen whose job is something else.
   *
   * Dismissal is remembered per site and per signal for the session, so it
   * cannot nag: dismissing "12 keywords have no facets" stays dismissed until
   * the situation changes into a different signal, or the tab is reopened.
   *
   * @param {number} trigger  bump after a write to re-read
   * @param {(screen: string) => void} go  navigates the app
   */
  function Nudge({ siteId, trigger = 0, go }) {
    const [sig, setSig] = useState(null);
    const [hidden, setHidden] = useState(false);

    const key = sig && `mlnudge:${siteId}:${sig.id}`;
    useEffect(() => {
      // trigger 0 is the initial mount: nothing has happened yet, so there is
      // nothing to point at.
      if (siteId == null || !trigger) return;
      let cancel = false;
      (async () => {
        try {
          const r = await api(`/inventory/nudge?site_id=${siteId}`);
          if (cancel) return;
          const s = r && r.signal;
          // A failed read shows nothing rather than a wrong nudge: this is an
          // aside on someone else's screen, so it must never be the thing that
          // reports a problem it is not sure about.
          setSig(s || null);
          setHidden(!!(s && sessionStorage.getItem(`mlnudge:${siteId}:${s.id}`)));
        } catch { if (!cancel) setSig(null); }
      })();
      return () => { cancel = true; };
    }, [siteId, trigger]);

    if (!sig || hidden) return null;
    const dest = MODULE_SCREEN[sig.module];
    const dismiss = () => { try { sessionStorage.setItem(key, "1"); } catch { /* private mode */ } setHidden(true); };

    return (
      <div className={`ds-nudge ds-nudge--${sig.tone}`}>
        <span className="ds-card__icon"><I name={TONE_ICON[sig.tone] || "circle"} size={15} /></span>
        <span className="ds-nudge__text"><b>{sig.title}</b>{sig.detail ? ` ${sig.detail}` : ""}</span>
        {dest && go && <button className="bi-btn bi-btn--sm" onClick={() => go(dest[0])}>Open the {dest[1]}</button>}
        <button className="bi-iconbtn" title="Dismiss" onClick={dismiss}><I name="x" size={15} /></button>
      </div>
    );
  }

  window.MlDiscovery = { DiscoveryStrip, Nudge };
})();
